import React, { useEffect } from "react"
import { makeStyles } from "tss-react/mui"
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons"
import { Box, DialogContent, Typography } from "@mui/material"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { TranslationKey } from "../../../../modules/localization/translations"
import { tailwindPalette } from "../../../../modules/theme/tailwindPalette"
import { StandardDialog } from "../StandardDialog"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import {
    ConfigQuery,
    fetchServiceCenterResourceDetails,
    ServiceCenterResourceDetailsResponse,
} from "../../createOrder/createOrderApi"
import { ServiceCenterResourceDetails } from "../../createOrder/createOrderTypes"

import { DynamicHtml } from "../../dynamic/DynamicHtml"
import { EmptyContent } from "../../skeleton/EmptyContent"
import { ResourceDetailsDialogSkeleton } from "./ResourceDetailsDialogSkeleton"

const useStyles = makeStyles()((theme) => ({
    dialogPaper: {
        maxWidth: "50rem",
    },
    dialogContent: {
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
        padding: "2rem 4rem 4rem",
        color: theme.palette.tailwind.slate[900],
    },
    imageWrap: {
        width: "100%",
        borderRadius: ".5rem",
        overflow: "hidden",
    },
    image: {
        display: "block",
        width: "100%",
        height: "auto",
        objectFit: "contain",
    },
    metadataGrid: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "2rem",
    },
    details: {
        color: theme.palette.tailwind.slate[700],
    },
    detailsListWrap: {
        display: "flex",
        alignItems: "center",
        gap: ".5rem",
        flexWrap: "wrap",
    },
    dot: {
        flex: "none",
        display: "block",
        width: ".5rem",
        height: ".5rem",
        borderRadius: "50%",
        backgroundColor: theme.palette.tailwind.slate[500],
    },
}))

type ListItem = Readonly<{
    name: string
}>

type ArrangementListProps = Readonly<{
    items: ListItem[]
}>

export function ArrangementList({ items }: ArrangementListProps) {
    const { classes } = useStyles()

    return (
        <Box className={classes.detailsListWrap}>
            {items.map((item, index) => (
                <React.Fragment key={item.name}>
                    <Typography variant="body1" className={classes.details}>
                        {item.name}
                    </Typography>
                    {index < items.length - 1 && <span className={classes.dot} />}
                </React.Fragment>
            ))}
        </Box>
    )
}

type DetailItemProps = Readonly<{
    labelId: TranslationKey
    labelFallback: string
    value?: React.ReactNode
    children?: React.ReactNode
}>

function DetailItem({ labelId, labelFallback, value, children }: DetailItemProps) {
    const { classes } = useStyles()

    return (
        <Box>
            <LocalizedStrict id={labelId}>
                <Typography variant="subtitle1" fontWeight={700}>
                    {labelFallback}
                </Typography>
            </LocalizedStrict>
            {value !== undefined ? (
                <Typography variant="body1" className={classes.details}>
                    {value}
                </Typography>
            ) : (
                children
            )}
        </Box>
    )
}

const transformData = (data: ServiceCenterResourceDetailsResponse): ServiceCenterResourceDetails | undefined =>
    data.resources?.[0]

type ResourceDetailsDialogProps = Readonly<{
    id: string
    handleClose: () => void
}>

export function ResourceDetailsDialog({ id, handleClose }: ResourceDetailsDialogProps) {
    const { data, isInitialLoading, isError } = useApiQuery(fetchServiceCenterResourceDetails, {
        queryName: ConfigQuery.RESOURCE_DETAILS,
        dependencies: { id },
        enabled: !!id,
        select: transformData,
    })

    useEffect(() => {
        if (isError) handleClose()
    }, [isError, handleClose])

    const { classes } = useStyles()

    return (
        <StandardDialog
            open={true}
            onClose={handleClose}
            dialogTitle={data?.name}
            slotProps={{
                paper: { className: classes.dialogPaper },
            }}
            maxWidthProp="md"
        >
            <DialogContent className={classes.dialogContent}>
                {isInitialLoading ? (
                    <ResourceDetailsDialogSkeleton />
                ) : !data ? (
                    <LocalizedStrict id="resource-details-dialog-empty" attrs={{ title: true, description: true }}>
                        <EmptyContent
                            icon={faCircleInfo}
                            backgroundColor={tailwindPalette.slate[400]}
                            title="No details available"
                            description="The details for this resource could not be found."
                        />
                    </LocalizedStrict>
                ) : (
                    <>
                        {data?.imageUrl && (
                            <div className={classes.imageWrap}>
                                <img src={data.imageUrl} className={classes.image} />
                            </div>
                        )}
                        {data?.description && (
                            <DetailItem labelId="resource-details-dialog-description" labelFallback="Description">
                                <DynamicHtml html={data.description} className={classes.details} />
                            </DetailItem>
                        )}
                        <div className={classes.metadataGrid}>
                            {data?.serviceCenterName && (
                                <DetailItem
                                    labelId="resource-details-dialog-service-center"
                                    labelFallback="Service center"
                                    value={data.serviceCenterName}
                                />
                            )}
                            {Number.isFinite(data?.room?.floor) && (
                                <DetailItem
                                    labelId="resource-details-dialog-floor"
                                    labelFallback="Floor"
                                    value={data?.room.floor}
                                />
                            )}
                            {Number.isFinite(data?.room?.squareMeters) && (
                                <DetailItem
                                    labelId="resource-details-dialog-square-meters"
                                    labelFallback="Square meters"
                                    value={data?.room.squareMeters}
                                />
                            )}
                            {data?.room?.walkDirection && (
                                <DetailItem
                                    labelId="resource-details-dialog-walk-direction"
                                    labelFallback="Walk direction"
                                    value={data.room.walkDirection}
                                />
                            )}
                            {Number.isFinite(data?.room?.seats) && (
                                <DetailItem labelId="resource-details-dialog-capacity" labelFallback="Capacity">
                                    <LocalizedStrict
                                        id="resource-details-dialog-capacity-quantity"
                                        vars={{ quantity: data?.room.seats ?? 0 }}
                                    >
                                        <Typography variant="body1" className={classes.details}>
                                            {data?.room.seats}
                                        </Typography>
                                    </LocalizedStrict>
                                </DetailItem>
                            )}
                            {!!data?.room?.tableSetups?.length && (
                                <DetailItem
                                    labelId="resource-details-dialog-table-arrangement"
                                    labelFallback="Table arrangement"
                                >
                                    <ArrangementList items={data.room.tableSetups} />
                                </DetailItem>
                            )}
                        </div>
                    </>
                )}
            </DialogContent>
        </StandardDialog>
    )
}
